import { useCallback, useEffect, useRef } from "react";
import {
  createPoint as apiCreatePoint,
  createProject as apiCreateProject,
  createProjectLog,
  createTeam as apiCreateTeam,
  deletePafta as apiDeletePafta,
  deleteProject as apiDeleteProject,
  deleteTeam as apiDeleteTeam,
  renameProject as apiRenameProject,
  saveMeasurementTemplate,
  updatePafta as apiUpdatePafta,
  updatePoint as apiUpdatePoint,
  updateTeam as apiUpdateTeam,
} from "../api";
import {
  getApiErrorMessage,
  getConflictCurrent,
  isConflictError,
} from "../api/client";
import {
  SYNC_CONFLICT_PREFIX,
  SYNC_QUEUE_PREFIX,
  getLegacySyncConflictKey,
  getLegacySyncQueueKey,
  getSyncConflictKey,
  getSyncQueueKey,
  getSyncQueuePrefix,
} from "../constants/storageKeys";
import {
  listQueueKeys,
  loadConflictsByKey as loadStoredConflicts,
  loadQueueByKey as loadStoredQueue,
  mutateConflictsByKey,
  mutateQueueByKey,
  requestPersistentOfflineStorage,
} from "../offline/offlineStore";
import {
  acknowledgeTask,
  createTaskEnvelope,
  mergeTaskIntoQueue,
  taskMatchesServerCurrent,
} from "../offline/syncQueueModel";

export function loadQueueByKey(queueKey, legacyKey = null) {
  return loadStoredQueue(queueKey, legacyKey);
}

export function loadConflictsByKey(conflictKey, legacyKey = null) {
  return loadStoredConflicts(conflictKey, legacyKey);
}

export function saveConflictsByKey(conflictKey, conflicts, legacyKey = null) {
  return mutateConflictsByKey(conflictKey, () => conflicts, legacyKey);
}

export function conflictKeyFromQueueKey(queueKey) {
  return queueKey.replace(SYNC_QUEUE_PREFIX, SYNC_CONFLICT_PREFIX);
}

function isDeleteTask(task) {
  return ["deleteProject", "deletePafta", "deleteTeam"].includes(task?.type);
}

async function runTask(task, selectedProjectId) {
  try {
    switch (task.type) {
      case "updatePoint":
        return await apiUpdatePoint(task.pointId, {
          ...(task.updates || {}),
          baseUpdatedAt: task.baseUpdatedAt ?? null,
        });
      case "createPoint":
        return await apiCreatePoint(task.projectId || selectedProjectId, task.data || {});
      case "createProject":
        return await apiCreateProject({
          id: task.projectId,
          ...(task.data || {}),
        });
      case "renameProject":
        return await apiRenameProject(
          task.projectId,
          task.data?.name || "",
          task.baseUpdatedAt ?? null
        );
      case "deleteProject":
        return await apiDeleteProject(task.projectId, task.baseUpdatedAt ?? null);
      case "updatePafta":
        return await apiUpdatePafta(task.paftaId, {
          ...(task.updates || {}),
          baseUpdatedAt: task.baseUpdatedAt ?? null,
        });
      case "deletePafta":
        return await apiDeletePafta(task.paftaId, task.baseUpdatedAt ?? null);
      case "createTeam":
        return await apiCreateTeam(task.projectId || selectedProjectId, task.data || {});
      case "updateTeam":
        return await apiUpdateTeam(task.teamId, {
          ...(task.data || {}),
          baseUpdatedAt: task.baseUpdatedAt ?? null,
        });
      case "deleteTeam":
        return await apiDeleteTeam(task.teamId, task.baseUpdatedAt ?? null);
      case "updateProjectTemplate":
        return await saveMeasurementTemplate(
          task.projectId,
          task.data?.measurementTemplate || [],
          task.baseUpdatedAt ?? null
        );
      case "createLog":
        return await createProjectLog(task.projectId || selectedProjectId, {
          ...(task.data || {}),
          clientRequestId: task.taskId,
        });
      default:
        throw new Error(`Bilinmeyen senkronizasyon görevi: ${task.type}`);
    }
  } catch (error) {
    // Silme isteği sunucuda tamamlanıp yanıt kaybolduysa sonraki 404 başarılı sayılır.
    if (isDeleteTask(task) && error?.response?.status === 404) {
      return { deleted: true };
    }
    throw error;
  }
}

export function useSyncQueue({
  isOnline,
  canSync = true,
  selectedProjectId,
  userId,
  setSyncPulse,
}) {
  const syncRunningRef = useRef(false);

  const processQueueByKey = useCallback(
    async (queueKey, legacyKey = null) => {
      let safety = 0;

      // Tek döngüde sınırsız iş yüklemek yerine makul bir üst sınırla UI'ı serbest bırakırız.
      while (safety < 500) {
        const queue = await loadStoredQueue(queueKey, legacyKey);
        if (!queue.length) return true;

        const rawTask = queue[0];
        const task = createTaskEnvelope(rawTask, userId);
        if (String(task.ownerUserId) !== String(userId || "")) {
          console.error("Başka kullanıcıya ait çevrimdışı görev işlenmedi.");
          return false;
        }

        if (!rawTask.taskId || !rawTask.ownerUserId || !rawTask.revision) {
          await mutateQueueByKey(
            queueKey,
            (latest) =>
              latest.map((item, index) => (index === 0 ? task : item)),
            legacyKey
          );
        }

        try {
          const result = await runTask(task, selectedProjectId);
          await mutateQueueByKey(
            queueKey,
            (latest) => acknowledgeTask(latest, task, result),
            legacyKey
          );
        } catch (error) {
          if (isConflictError(error)) {
            const current = getConflictCurrent(error);

            // Yanıt kaybolmuş olabilir: sunucudaki değer hedefle aynıysa görevi tekrar uygulamayız.
            if (taskMatchesServerCurrent(task, current)) {
              await mutateQueueByKey(
                queueKey,
                (latest) => acknowledgeTask(latest, task, current),
                legacyKey
              );
              safety += 1;
              continue;
            }

            let removedExactRevision = false;
            await mutateQueueByKey(
              queueKey,
              (latest) => {
                const latestTask = latest.find((item) => item.taskId === task.taskId);
                if (
                  latestTask &&
                  Number(latestTask.revision || 1) === Number(task.revision || 1)
                ) {
                  removedExactRevision = true;
                  return latest.filter((item) => item.taskId !== task.taskId);
                }
                return latest;
              },
              legacyKey
            );

            if (!removedExactRevision) {
              safety += 1;
              continue;
            }

            const conflictKey = getSyncConflictKey(userId, task.projectId || selectedProjectId);
            const conflictEntry = {
              id: task.taskId,
              task,
              message: getApiErrorMessage(
                error,
                "Veri başka bir oturumda değiştiği için bu kayıt otomatik işlenemedi."
              ),
              current,
              createdAt: Date.now(),
            };
            await mutateConflictsByKey(
              conflictKey,
              (conflicts) => [
                conflictEntry,
                ...conflicts.filter((item) => item.id !== conflictEntry.id),
              ],
              getLegacySyncConflictKey(task.projectId || selectedProjectId)
            );

            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("geo-sync-conflict", { detail: conflictEntry })
              );
            }

            setSyncPulse((value) => value + 1);
            safety += 1;
            continue;
          }

          console.error("Senkronizasyon hatası:", error);
          return false;
        }

        safety += 1;
      }

      return false;
    },
    [selectedProjectId, setSyncPulse, userId]
  );

  const processAllSyncQueues = useCallback(async () => {
    if (!isOnline || !canSync || !userId || syncRunningRef.current) return false;

    syncRunningRef.current = true;
    let completed = true;

    try {
      const prefix = getSyncQueuePrefix(userId);
      const currentKey = selectedProjectId
        ? getSyncQueueKey(userId, selectedProjectId)
        : null;

      if (currentKey) {
        await loadStoredQueue(currentKey, getLegacySyncQueueKey(selectedProjectId));
      }

      const keys = await listQueueKeys(prefix);
      const initialQueues = await Promise.all(keys.map((key) => loadStoredQueue(key)));
      const initialPendingCount = initialQueues.reduce(
        (total, queue) => total + queue.length,
        0
      );
      keys.sort((left, right) => {
        if (left === currentKey && right !== currentKey) return -1;
        if (right === currentKey && left !== currentKey) return 1;
        return left.localeCompare(right);
      });

      for (const key of keys) {
        const legacyKey = key === currentKey
          ? getLegacySyncQueueKey(selectedProjectId)
          : null;
        const result = await processQueueByKey(key, legacyKey);
        if (!result) {
          completed = false;
          break;
        }
      }

      const remainingQueues = await Promise.all(keys.map((key) => loadStoredQueue(key)));
      const remainingPendingCount = remainingQueues.reduce(
        (total, queue) => total + queue.length,
        0
      );
      if (remainingPendingCount !== initialPendingCount) {
        setSyncPulse((value) => value + 1);
      }
    } finally {
      syncRunningRef.current = false;
    }

    return completed;
  }, [canSync, isOnline, processQueueByKey, selectedProjectId, setSyncPulse, userId]);

  const addToSyncQueue = useCallback(
    async (task, queueForProjectId = null) => {
      const projectId = queueForProjectId || task.projectId || selectedProjectId;
      if (!projectId || !userId) {
        throw new Error("Çevrimdışı kayıt için kullanıcı ve proje bilgisi gereklidir.");
      }

      const queueKey = getSyncQueueKey(userId, projectId);
      const legacyKey = getLegacySyncQueueKey(projectId);
      const envelope = createTaskEnvelope({ ...task, projectId }, userId);

      await requestPersistentOfflineStorage();
      await mutateQueueByKey(
        queueKey,
        (current) => mergeTaskIntoQueue(current, envelope),
        legacyKey
      );
      setSyncPulse((value) => value + 1);

      if (isOnline && canSync) {
        processAllSyncQueues().catch((error) => {
          console.error("Senkronizasyon kuyruğu çalıştırılamadı:", error);
        });
      }

      return envelope.taskId;
    },
    [canSync, isOnline, processAllSyncQueues, selectedProjectId, setSyncPulse, userId]
  );

  useEffect(() => {
    if (!isOnline || !canSync || !userId) return undefined;

    let cancelled = false;
    let timerId = null;
    let retryDelay = 2500;

    const tick = async () => {
      if (cancelled) return;

      try {
        const completed = await processAllSyncQueues();
        retryDelay = completed ? 2500 : Math.min(retryDelay * 2, 60000);
      } catch (error) {
        retryDelay = Math.min(retryDelay * 2, 60000);
        console.error("Senkronizasyon döngüsü hatası:", error);
      }

      const keys = await listQueueKeys(getSyncQueuePrefix(userId));
      const queues = await Promise.all(keys.map((key) => loadStoredQueue(key)));
      const hasPendingTask = queues.some((queue) => queue.length > 0);

      if (!cancelled) {
        // navigator.onLine yalnız ağ arayüzünü gösterir. API geçici olarak kapalıyken
        // kuyruk boş görünse bile düzenli denetim yeni görevlerin takılı kalmasını önler.
        timerId = window.setTimeout(tick, hasPendingTask ? retryDelay : 15000);
      }
    };

    tick();
    const retryNow = () => {
      if (timerId) window.clearTimeout(timerId);
      retryDelay = 2500;
      tick();
    };
    window.addEventListener("focus", retryNow);

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
      window.removeEventListener("focus", retryNow);
    };
  }, [canSync, isOnline, processAllSyncQueues, setSyncPulse, userId]);

  return { addToSyncQueue, processAllSyncQueues };
}
